import { useCallback, useEffect, useRef, useState, type SetStateAction } from "react";
import { Grid2, useMediaQuery, useTheme } from "@mui/material";
import { useSelector } from "../../app/helpers";
import { RootState } from "../../app/rootReducer";
import { useRemoteFetchNotificationSettings } from "../../hooks/Remote/Notification/useRemoteFetchNotificationSettings";
import { useRemotePatchNotificationSettings } from "../../hooks/Remote/Notification/useRemotePatchNotificationSettings";
import { BookingType } from "../../features/Booking-Form/typings/booking-inputs";
import {
  NotificationDataType,
  NotificationSettingOptions,
  RemoteNotificationSetting
} from "./Domain/NotificationSettings.types";
import DefaultSpinner from "../LoadingSpinner/default-spinner.component";
import NotificationCardList from "./partial/NotificationCardList.partial";
import { shouldIncludeNotificationOption } from "./functions/shouldIncludeNotificationOption.function";
import {
  approvalNotiTypes,
  bookingNotiTypes,
  serviceNotiTypes,
  supervisorNotiTypes,
  timeRelatedNotiTypes,
  connectionNotiTypes,
  fleetNotiTypes
} from "./Domain/NotificationSettings.data";
import type { ServiceHierarchyNode } from "./Domain/serviceNotificationHierarchy.types";
import {
  buildAllLeavesEnabledMap,
  buildApprovalHierarchyRootFromRemote,
  buildInitialEnabledMapForApprovalLeaves,
  parseBookableInventoryIdFromLeafId
} from "./functions/approval-location-hierarchy.mapper";
import { collectAllLeafIds } from "./functions/serviceNotificationHierarchy.helpers";
import type { UpdateNotificationSettingsDto } from "../../hooks/Remote/Notification/useRemotePatchNotificationSettings";

/** Clears every approval leaf toggle; kept at module scope to avoid deep nesting (Sonar S2004). */
function disableAllApprovalLeaves(prev: Record<string, boolean>): Record<string, boolean> {
  const next: Record<string, boolean> = {};
  for (const key of Object.keys(prev)) {
    next[key] = false;
  }
  return next;
}

export function NotificationSettingList() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const {
    userRoles: { isTenantAdmin, isCompanyAdmin, isRequestAdmin, isFleetAdmin },
    userInformation: {
      placeTypes,
      company: {
        meta: { teamBookingAllowed, activateBookingServices }
      }
    }
  } = useSelector((state: RootState) => state.login);

  const fleetEnabledForTenant =
    placeTypes?.some((type: { name: string }) => type.name === BookingType.VEHICLE) ?? false;

  const [notificationOptions, setNotificationOptions] = useState<NotificationSettingOptions[]>([]);

  const timeRelatedNotis = notificationOptions.filter(option =>
    timeRelatedNotiTypes.includes(option.name as NotificationDataType)
  );

  const [switches, setSwitches] = useState<Record<string, boolean>>(
    notificationOptions.reduce((acc, _, index) => ({ ...acc, [index]: false }), {})
  );
  const [timeSwitches, setTimeSwitches] = useState<Record<string, number>>(
    timeRelatedNotis.reduce((acc, _, index) => ({ ...acc, [index]: 10 }), {})
  );

  const { data: notificationSettingsData, refetch: refetchNotificationSettings } =
    useRemoteFetchNotificationSettings();
  const { mutateAsync: patchNotificationSettings } = useRemotePatchNotificationSettings();

  const [approvalHierarchyRoot, setApprovalHierarchyRoot] = useState<ServiceHierarchyNode | null>(
    null
  );
  const [approvalLeafEnabled, setApprovalLeafEnabled] = useState<Record<string, boolean>>({});
  const isHydratingFromRemoteRef = useRef(false);
  const approvalHierarchyRootRef = useRef<ServiceHierarchyNode | null>(null);
  approvalHierarchyRootRef.current = approvalHierarchyRoot;

  const syncApprovalLeavesForAdminToggle = useCallback((isAdmin: boolean) => {
    queueMicrotask(() => {
      if (isAdmin) {
        const root = approvalHierarchyRootRef.current;
        if (root) {
          setApprovalLeafEnabled(buildAllLeavesEnabledMap(collectAllLeafIds(root)));
        }
      } else {
        setApprovalLeafEnabled(disableAllApprovalLeaves);
      }
    });
  }, []);

  const setSwitchesWithApproval = useCallback(
    (update: SetStateAction<Record<string, boolean>>) => {
      /* istanbul ignore next */
      setSwitches(prev => {
        const next = typeof update === "function" ? update(prev) : update;
        if (!isHydratingFromRemoteRef.current) {
          const wasAdmin = prev[NotificationDataType.BOOKING_APPROVAL_ADMIN] === true;
          const isAdmin = next[NotificationDataType.BOOKING_APPROVAL_ADMIN] === true;
          if (wasAdmin !== isAdmin) {
            syncApprovalLeavesForAdminToggle(isAdmin);
          }
        }
        return next;
      });
    },
    [syncApprovalLeavesForAdminToggle]
  );

  const bookingNotis = notificationOptions.filter(option =>
    bookingNotiTypes.includes(option.name as NotificationDataType)
  );
  const approvalNotis = notificationOptions.filter(option =>
    approvalNotiTypes.includes(option.name as NotificationDataType)
  );
  const superviosrNotis = notificationOptions.filter(option =>
    supervisorNotiTypes.includes(option.name as NotificationDataType)
  );
  const serviceNotis = notificationOptions.filter(option =>
    serviceNotiTypes.includes(option.name as NotificationDataType)
  );
  const connectionNotis = notificationOptions.filter(option =>
    connectionNotiTypes.includes(option.name as NotificationDataType)
  );
  const fleetNotis = notificationOptions
    .filter(option => fleetNotiTypes.includes(option.name as NotificationDataType))
    .sort((leftOption, rightOption) => {
      const leftIndex = fleetNotiTypes.indexOf(leftOption.name as NotificationDataType);
      const rightIndex = fleetNotiTypes.indexOf(rightOption.name as NotificationDataType);

      return leftIndex - rightIndex;
    });

  /**
   * later, include time switch values in the Patch body
   */
  const updateNotificationSettingSwitch = () => {
    const selectedItems: string[] = Object.keys(switches).filter(key => switches[key]);

    const bookingStartReminderMinutes = timeSwitches[NotificationDataType.BOOKING_START_REMINDER];
    const bookingCheckInReminderMinutes =
      timeSwitches[NotificationDataType.BOOKING_CHECKIN_REMINDER];
    const fleetBookingStartReminderMinutes =
      timeSwitches[NotificationDataType.FLEET_BOOKING_START_REMINDER];
    const fleetBookingEndReminderMinutes =
      timeSwitches[NotificationDataType.FLEET_BOOKING_END_REMINDER];
    const fleetAdminBookingStartReminderMinutes =
      timeSwitches[NotificationDataType.FLEET_ADMIN_BOOKING_START_REMINDER];
    const fleetAdminBookingEndReminderMinutes =
      timeSwitches[NotificationDataType.FLEET_ADMIN_BOOKING_END_REMINDER];

    const adminOn = switches[NotificationDataType.BOOKING_APPROVAL_ADMIN] === true;

    const patchNotiBody: UpdateNotificationSettingsDto = {
      enabledNotifications: selectedItems,
      ...(bookingStartReminderMinutes !== undefined && {
        bookingStartReminderMinutes
      }),
      ...(bookingCheckInReminderMinutes !== undefined && {
        bookingCheckInReminderMinutes
      }),
      ...(fleetBookingStartReminderMinutes !== undefined && {
        fleetBookingStartReminderMinutes
      }),
      ...(fleetBookingEndReminderMinutes !== undefined && {
        fleetBookingEndReminderMinutes
      }),
      ...(fleetAdminBookingStartReminderMinutes !== undefined && {
        fleetAdminBookingStartReminderMinutes
      }),
      ...(fleetAdminBookingEndReminderMinutes !== undefined && {
        fleetAdminBookingEndReminderMinutes
      })
    };

    if (!adminOn) {
      patchNotiBody.approvalNotificationBookingInventoryIds = null;
    } else if (approvalHierarchyRoot) {
      const leafIds = collectAllLeafIds(approvalHierarchyRoot);
      const enabledNumericIds = leafIds
        .filter(id => approvalLeafEnabled[id])
        .map(id => parseBookableInventoryIdFromLeafId(id))
        .filter((n): n is number => n !== null);
      // Always send explicit IDs when the tree exists so PATCH reflects the selection (including
      // "all checked"). Backend treats null/empty as unscoped; a full whitelist is equivalent for
      // current rows and keeps the request body aligned with the UI.
      patchNotiBody.approvalNotificationBookingInventoryIds =
        enabledNumericIds.length > 0 ? enabledNumericIds : null;
    } else {
      patchNotiBody.approvalNotificationBookingInventoryIds = null;
    }

    patchNotificationSettings(patchNotiBody).then(() => refetchNotificationSettings());
  };

  const handleInitialEnabledSettings = (remote: RemoteNotificationSetting) => {
    if (!remote) return;

    const {
      enabledNotifications,
      notificationOptions,
      bookingStartReminderMinutes = 10,
      bookingCheckInReminderMinutes = 10,
      fleetBookingStartReminderMinutes = 10,
      fleetBookingEndReminderMinutes = 10,
      fleetAdminBookingStartReminderMinutes = 10,
      fleetAdminBookingEndReminderMinutes = 10
    } = remote;

    const deactivated = notificationOptions.filter(opt => !enabledNotifications.includes(opt.name));

    const timeMinutesMap: Record<string, number> = {
      [NotificationDataType.BOOKING_START_REMINDER]: bookingStartReminderMinutes,
      [NotificationDataType.BOOKING_CHECKIN_REMINDER]: bookingCheckInReminderMinutes,
      [NotificationDataType.FLEET_BOOKING_START_REMINDER]: fleetBookingStartReminderMinutes,
      [NotificationDataType.FLEET_BOOKING_END_REMINDER]: fleetBookingEndReminderMinutes,
      [NotificationDataType.FLEET_ADMIN_BOOKING_START_REMINDER]:
        fleetAdminBookingStartReminderMinutes,
      [NotificationDataType.FLEET_ADMIN_BOOKING_END_REMINDER]: fleetAdminBookingEndReminderMinutes
    };

    for (const enabled of enabledNotifications) {
      setSwitchesWithApproval(prev => ({ ...prev, [`${enabled}`]: true }));
      setTimeSwitches(prev => ({
        ...prev,
        [`${enabled}`]: timeMinutesMap[enabled] ?? 10
      }));
    }
    for (const deact of deactivated) {
      setSwitchesWithApproval(prev => ({ ...prev, [`${deact.name}`]: false }));
    }
  };

  useEffect(() => {
    if (!notificationSettingsData) return;

    isHydratingFromRemoteRef.current = true;

    setNotificationOptions(
      notificationSettingsData.notificationOptions
        .filter(opt =>
          shouldIncludeNotificationOption(opt, {
            isTenantAdmin,
            isCompanyAdmin,
            isRequestAdmin,
            isFleetAdmin,
            teamBookingAllowed,
            activateBookingServices,
            fleetEnabledForTenant
          })
        )
        .map(opt => ({ name: opt.name, en: opt.en, de: opt.de }))
    );

    const root = buildApprovalHierarchyRootFromRemote(notificationSettingsData.locationHierarchy);
    approvalHierarchyRootRef.current = root;
    setApprovalHierarchyRoot(root);
    if (root) {
      const leafIds = collectAllLeafIds(root);
      setApprovalLeafEnabled(
        buildInitialEnabledMapForApprovalLeaves(
          leafIds,
          notificationSettingsData.approvalNotificationBookingInventoryIds
        )
      );
    } else {
      setApprovalLeafEnabled({});
    }

    handleInitialEnabledSettings(notificationSettingsData);

    queueMicrotask(() => {
      isHydratingFromRemoteRef.current = false;
    });
  }, [notificationSettingsData]);

  return (
    <Grid2
      container
      data-testid="notification-setting-options-group-list"
      sx={[isMobile ? { width: "95%" } : { width: "100%" }]}
    >
      {!notificationOptions.length && <DefaultSpinner />}

      {notificationOptions && notificationOptions.length > 0 && (
        <NotificationCardList
          notificationOptions={{
            bookingOptions: bookingNotis,
            approvalOptions: approvalNotis,
            supervisorOptions: superviosrNotis,
            serviceOptions: serviceNotis,
            connectionOptions: connectionNotis,
            fleetOptions: fleetNotis
          }}
          switches={switches}
          setSwitches={setSwitchesWithApproval}
          timeSwitches={timeSwitches}
          setTimeSwitches={setTimeSwitches}
          handleUpdateSettings={updateNotificationSettingSwitch}
          approvalHierarchyRoot={approvalHierarchyRoot}
          approvalLeafEnabled={approvalLeafEnabled}
          setApprovalLeafEnabled={setApprovalLeafEnabled}
        />
      )}
    </Grid2>
  );
}
